import axios from 'axios'
import apiResponseEnum from '@/api/enums/apiResponseEnum.js'
import i18n from '@/lang/index.js'
import { useAuthStore } from '@/stores/authStore.js'

const errorCodes = new Set([apiResponseEnum.FORBIDDEN, apiResponseEnum.UNAUTHORIZED])
const baseUrl = `${import.meta.env.VITE_API_BASE_URL}${import.meta.env.VITE_API_PREFIX ?? ''}` || ''
const timeout = Number.parseInt(import.meta.env.VITE_API_TIMEOUT) || 30_000

const stripTrailingSlash = str => {
  return str.replace(/\/\/*/g, '/').replace(/\/+$/, '')
}

export const HTTP = {
  /**
   * Axios client
   */
  client: axios.create({
    baseURL     : baseUrl,
    timeout     : timeout,
    responseType: 'json',
    crossDomain : true,
    // withCredentials: true,
    headers     : {
      'X-Requested-With': 'XMLhttpRequest'
    }
  }),

  /**
   * request
   *
   * @param endpoint
   * @param data
   * @param config
   *
   * @returns {Promise<void>}
   * @constructor
   */
  async request (endpoint, data = {}, config = {}) {
    if (!endpoint.method) {
      endpoint.method = HTTP.method.post
    }

    endpoint.path = stripTrailingSlash(endpoint.path)

    switch (endpoint.method) {
      case HTTP.method.get:
      case HTTP.method.delete: {
        return await HTTP.client[endpoint.method](endpoint.path, { params: { ...data }, ...config })
      }
      case HTTP.method.post:
      case HTTP.method.put:
      case HTTP.method.patch: {
        return await HTTP.client[endpoint.method](endpoint.path, data, { params: {}, ...config })
      }
    }
  },

  /**
   * requestAll
   *
   * @param requests {Array<Request>}
   * @returns A new Promise. {Promise<unknown[]>}
   * @constructor
   */
  async requestAll (requests) {
    return await Promise.all(requests)
  },

  /**
   * method
   *
   * @returns {{delete: string, post: string, get: string, patch: string, put: string}}
   * @constructor
   */
  get method () {
    return {
      get   : 'get',
      post  : 'post',
      put   : 'put',
      patch : 'patch',
      delete: 'delete'
    }
  }
}

HTTP.client.interceptors.request.use(requestConfig => {
  const defaultRequestParams = {}
  const defaultRequestHeaders = {
    'Accept-Language': i18n.code || import.meta.env.VITE_I18N_LOCALE
  }

  requestConfig.headers = Object.assign({}, defaultRequestHeaders || {}, requestConfig.headers || {})
  requestConfig.params = Object.assign({}, defaultRequestParams || {}, requestConfig.params || {})

  return requestConfig
}, function (error) {
  return Promise.reject(error)
})

HTTP.client.interceptors.response.use(response => {
  return response
}, function (error) {
  if (error && (error.message === 'Network Error' || errorCodes.has(error?.response?.status))) {
    const router = inject('router')
    const authStore = useAuthStore()
    authStore.$reset()

    if (router && router.currentRoute.name !== 'Login') {
      router.push({
        name : 'Login',
        query: {}
      })
    }
  }
  return Promise.reject(error)
})

export default HTTP
